import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export type ManualRunState = 'failed' | 'success';

interface Props {
  entityId: string;
  disabled?: boolean;
  onChange: (state: ManualRunState) => void | Promise<void>;
}

const MarkStateDropdownMAIQ: React.FC<Props> = ({
  entityId,
  disabled,
  onChange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);
  const buttonId = `mark-state-${entityId}`;
  const menuId = `${buttonId}-menu`;

  const selectState = async (state: ManualRunState) => {
    setAnchor(null);
    setSaving(true);
    try {
      await onChange(state);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Button
        id={buttonId}
        variant="outlined"
        size="small"
        disabled={disabled || saving}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={Boolean(anchor)}
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          height: 28,
          whiteSpace: 'nowrap',
          textTransform: 'none',
          backgroundColor: '#fff',
          borderColor: '#dbe2ea',
          color: '#52627a',
          borderRadius: '6px',
          fontSize: 12,
          fontWeight: 500,
          px: 1.25,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#f4f7fb',
            borderColor: '#b8c5d6',
            boxShadow: 'none',
          },
        }}
      >
        Mark state as…
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ 'aria-labelledby': buttonId }}
        PaperProps={{
          sx: {
            minWidth: 165,
            mt: 1,
            borderRadius: '8px',
            border: '1px solid #dce3ed',
            '& .MuiMenu-list': { p: 0.5 },
          },
        }}
      >
        {(['failed', 'success'] as ManualRunState[]).map((state) => (
          <MenuItem
            key={state}
            disabled={disabled || saving}
            onClick={() => {
              void selectState(state);
            }}
            sx={{
              gap: 0.75,
              fontSize: 14,
              minHeight: 30,
              px: 0.75,
              py: 0.5,
              '&:hover, &.Mui-focusVisible': { backgroundColor: '#edf2f7' },
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '1px',
                backgroundColor: state === 'failed' ? '#ff0000' : '#178900',
              }}
            />
            {state}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default MarkStateDropdownMAIQ;
